import React, { useEffect, useMemo, useState, useRef } from 'react';
import { fmtMoney } from '../utils/formatters';
import { RowData } from '../utils/roiCalculations';
interface ProfitChartProps {
  rows: RowData[];
  breakeven: string;
}
type ChartMode = 'cumulative' | 'monthly' | 'roi';
// Helper to calculate nice round numbers for axis labels
function getNiceAxisValues(min: number, max: number): number[] {
  const range = max - min;
  // Determine step size based on range
  let step: number;
  if (range <= 100000) {
    step = 25000; // 25K increments for smaller ranges
  } else if (range <= 250000) {
    step = 50000; // 50K increments
  } else if (range <= 500000) {
    step = 100000; // 100K increments
  } else if (range <= 1000000) {
    step = 200000; // 200K increments
  } else {
    step = 250000; // 250K increments for large ranges
  }
  // Round min down to nearest step
  const axisMin = Math.floor(min / step) * step;
  // Round max up to nearest step
  const axisMax = Math.ceil(max / step) * step;
  // Generate 5 evenly spaced values
  const values: number[] = [];
  for (let i = 0; i <= 4; i++) {
    values.push(axisMin + (axisMax - axisMin) * (i / 4));
  }
  return values;
}
export function ProfitChart({
  rows,
  breakeven
}: ProfitChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [mode, setMode] = useState<ChartMode>('roi');
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [mousePos, setMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossRevenue = row.grossRevenue * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        // Halve all active counts
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        // Recalculate sales based on reduced active reps
        // This compounds with halfSales if both are active
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossRevenue = row.grossRevenue * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
      }
      // Recalculate net profit and cumulative profit
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  // Recalculate cumulative profit for adjusted rows
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  // Calculate cumulative totals for tooltip
  const getCumulativeTotals = (index: number) => {
    const upToIndex = finalRows.slice(0, index + 1);
    const totalInvestment = upToIndex.reduce((sum, r) => sum + r.totalCost, 0);
    const totalGrossRevenue = upToIndex.reduce((sum, r) => sum + r.grossRevenue, 0);
    const totalProfit = upToIndex.reduce((sum, r) => sum + r.netProfit, 0);
    return {
      period: finalRows[index].period,
      totalInvestment,
      totalRevenue: totalGrossRevenue,
      totalProfit,
      monthlyInvestment: finalRows[index].totalCost,
      monthlyRevenue: finalRows[index].grossProfit,
      monthlyProfit: finalRows[index].netProfit
    };
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = 400 * dpr;
    ctx.scale(dpr, dpr);
    const w = rect.width;
    const h = 400;
    ctx.clearRect(0, 0, w, h);
    const padding = {
      l: 80,
      r: 16,
      t: 16,
      b: 32
    };
    const innerW = w - padding.l - padding.r;
    const innerH = h - padding.t - padding.b;
    // Determine which rows to show based on mode
    let displayRows = finalRows;
    if (mode === 'roi') {
      // Show first 6 months (Launch + months 1-6)
      displayRows = finalRows.slice(0, 7);
    }
    let data1: number[];
    let data2: number[] | null = null;
    let minV: number, maxV: number;
    if (mode === 'cumulative') {
      data1 = finalRows.map(r => r.cumProfit);
      // Add cumulative gross revenue as second line (sales × ticket, before margin)
      const cumulativeRevenue: number[] = [];
      let sumRevenue = 0;
      finalRows.forEach(r => {
        sumRevenue += r.grossRevenue;
        cumulativeRevenue.push(sumRevenue);
      });
      data2 = cumulativeRevenue;
      // Force baseline at 0, not negative
      minV = Math.min(0, ...data1, ...data2);
      maxV = Math.max(...data1, ...data2);
    } else if (mode === 'roi') {
      // ROI mode: show cumulative investment vs cumulative revenue (first 6 months)
      const cumulativeInvestment: number[] = [];
      const cumulativeRevenue: number[] = [];
      let sumInvestment = 0;
      let sumRevenue = 0;
      displayRows.forEach(r => {
        sumInvestment += r.totalCost;
        sumRevenue += r.grossRevenue;
        cumulativeInvestment.push(sumInvestment);
        cumulativeRevenue.push(sumRevenue);
      });
      data1 = cumulativeInvestment;
      data2 = cumulativeRevenue;
      minV = Math.min(0, ...data1, ...data2);
      maxV = Math.max(...data1, ...data2);
    } else {
      // Monthly mode: show investment vs revenue
      data1 = finalRows.map(r => r.totalCost);
      data2 = finalRows.map(r => r.grossProfit);
      minV = Math.min(0, ...data1, ...data2);
      maxV = Math.max(...data1, ...data2);
    }
    // Get nice axis values
    const axisValues = getNiceAxisValues(minV, maxV);
    const lo = axisValues[0];
    const hi = axisValues[axisValues.length - 1];
    const dataLength = mode === 'roi' ? displayRows.length : finalRows.length;
    const x = (i: number) => padding.l + i / (dataLength - 1) * innerW;
    const y = (v: number) => padding.t + (1 - (v - lo) / (hi - lo)) * innerH;
    // Axes
    ctx.strokeStyle = '#E2E8F0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.l, padding.t);
    ctx.lineTo(padding.l, padding.t + innerH);
    ctx.lineTo(padding.l + innerW, padding.t + innerH);
    ctx.stroke();
    // Zero line
    if (lo < 0 && hi > 0) {
      const y0 = y(0);
      ctx.strokeStyle = '#CBD5E1';
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padding.l, y0);
      ctx.lineTo(padding.l + innerW, y0);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (mode === 'cumulative') {
      // Draw cumulative profit line (amber/orange)
      ctx.strokeStyle = '#F59E0B';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw cumulative gross revenue line (green)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Points for cumulative profit (amber)
      ctx.fillStyle = '#F59E0B';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        // White ring on hover
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for cumulative revenue (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else if (mode === 'roi') {
      // Draw cumulative investment line (red)
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw cumulative revenue line (green)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Find crossover point and highlight it
      if (data2) {
        for (let i = 1; i < data1.length; i++) {
          if (data2[i] >= data1[i] && data2[i - 1] < data1[i - 1]) {
            // Crossover detected! Draw a highlight
            const px = x(i);
            const py = y(data2[i]);
            // Draw a star or highlight circle
            ctx.fillStyle = '#F59E0B';
            ctx.beginPath();
            ctx.arc(px, py, 10, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 3;
            ctx.stroke();
            // Draw vertical dashed line
            ctx.strokeStyle = '#F59E0B';
            ctx.setLineDash([5, 5]);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(px, padding.t);
            ctx.lineTo(px, padding.t + innerH);
            ctx.stroke();
            ctx.setLineDash([]);
            // Add "ROI Achieved!" label
            ctx.fillStyle = '#F59E0B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('ROI Achieved!', px, padding.t - 5);
            break;
          }
        }
      }
      // Points for investment (red)
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for revenue (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else {
      // Monthly mode
      // Draw monthly investment line (red)
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw monthly revenue line (green)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Points for investment (red)
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for revenue (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    }
    // Y-Axis Labels - use nice axis values
    ctx.fillStyle = '#475569';
    ctx.font = '600 13px Inter, sans-serif';
    ctx.textAlign = 'right';
    axisValues.forEach(tv => {
      const py = y(tv);
      ctx.fillText(fmtMoney(tv), padding.l - 10, py + 4);
    });
    // X-Axis Labels
    ctx.fillStyle = '#475569';
    ctx.font = '600 13px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Launch', padding.l, h - 10);
    ctx.textAlign = 'right';
    if (mode === 'roi') {
      ctx.fillText('M6', padding.l + innerW, h - 10);
    } else {
      ctx.fillText('M12', padding.l + innerW, h - 10);
    }
  }, [finalRows, mode, hoveredPoint]);
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const padding = {
      l: 80,
      r: 16,
      t: 16,
      b: 32
    };
    const innerW = rect.width - padding.l - padding.r;
    const displayRows = mode === 'roi' ? finalRows.slice(0, 7) : finalRows;
    const dataLength = displayRows.length;
    // Find closest point
    let closestIndex = -1;
    let closestDist = Infinity;
    displayRows.forEach((_, i) => {
      const px = padding.l + i / (dataLength - 1) * innerW;
      const dist = Math.abs(mouseX - px);
      if (dist < closestDist && dist < 20) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    setHoveredPoint(closestIndex >= 0 ? closestIndex : null);
    setMousePos(closestIndex >= 0 ? {
      x: e.clientX,
      y: e.clientY
    } : null);
  };
  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setMousePos(null);
  };
  const tooltipData = hoveredPoint !== null ? getCumulativeTotals(hoveredPoint) : null;
  return <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-113">
      <div className="flex items-center justify-between mb-3" data-id="element-114">
        <div className="text-sm text-gray-600 font-semibold" data-id="element-115">
          {mode === 'cumulative' ? 'Cumulative Net Profit' : mode === 'roi' ? 'ROI Break-Even Analysis (First 6 Months)' : 'Monthly Investment vs Revenue'}
        </div>

        {/* Toggle */}
        <div className="flex gap-2 bg-white border-2 border-gray-200 rounded p-1" data-id="element-116">
          <button onClick={() => setMode('roi')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'roi' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-117">
            ROI
          </button>
          <button onClick={() => setMode('monthly')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'monthly' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-118">
            Monthly
          </button>
          <button onClick={() => setMode('cumulative')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'cumulative' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-119">
            Cumulative
          </button>
        </div>
      </div>

      {/* Scenario Toggles */}
      <div className="flex gap-3 mb-3" data-id="element-120">
        <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-121">
          {halfSales ? '✓ ' : ''}50% Sales Scenario
        </button>
        <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-122">
          {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
        </button>
      </div>

      {mode === 'monthly' && <div className="flex gap-4 mb-3 text-xs" data-id="element-123">
          <div className="flex items-center gap-2" data-id="element-124">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-125"></div>
            <span className="text-gray-600 font-semibold" data-id="element-126">
              Monthly Investment
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-127">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-128"></div>
            <span className="text-gray-600 font-semibold" data-id="element-129">Monthly Revenue</span>
          </div>
        </div>}

      {mode === 'cumulative' && <div className="flex gap-4 mb-3 text-xs" data-id="element-130">
          <div className="flex items-center gap-2" data-id="element-131">
            <div className="w-3 h-3 rounded-full bg-amber" data-id="element-132"></div>
            <span className="text-gray-600 font-semibold" data-id="element-133">
              Cumulative Net Profit
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-134">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-135"></div>
            <span className="text-gray-600 font-semibold" data-id="element-136">
              Cumulative Gross Revenue
            </span>
          </div>
        </div>}

      {mode === 'roi' && <div className="flex gap-4 mb-3 text-xs" data-id="element-137">
          <div className="flex items-center gap-2" data-id="element-138">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-139"></div>
            <span className="text-gray-600 font-semibold" data-id="element-140">
              Cumulative Investment
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-141">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-142"></div>
            <span className="text-gray-600 font-semibold" data-id="element-143">
              Cumulative Revenue
            </span>
          </div>
        </div>}

      <div ref={containerRef} className="relative" data-id="element-144">
        <canvas ref={canvasRef} className="w-full h-[400px] block cursor-crosshair" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} data-id="element-145" />

        {/* Tooltip */}
        {tooltipData && mousePos && <div className="fixed z-50 pointer-events-none" style={{
        left: mousePos.x + 16,
        top: mousePos.y - 16
      }} data-id="element-146">
            <div className="bg-navy text-white rounded shadow-xl border-2 border-amber p-4 min-w-[280px]" data-id="element-147">
              <div className="font-bold text-amber mb-2 text-sm" data-id="element-148">
                {tooltipData.period}
              </div>

              {mode === 'cumulative' ? <>
                  <div className="space-y-1.5 text-xs" data-id="element-149">
                    <div className="flex justify-between gap-4" data-id="element-150">
                      <span className="text-gray-300" data-id="element-151">
                        Cumulative Investment:
                      </span>
                      <span className="font-bold" data-id="element-152">
                        {fmtMoney(tooltipData.totalInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-153">
                      <span className="text-gray-300" data-id="element-154">
                        Cumulative Gross Revenue:
                      </span>
                      <span className="font-bold text-green-400" data-id="element-155">
                        {fmtMoney(tooltipData.totalRevenue)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-156"></div>
                    <div className="flex justify-between gap-4" data-id="element-157">
                      <span className="text-gray-300" data-id="element-158">
                        Cumulative Net Profit:
                      </span>
                      <span className={`font-bold ${tooltipData.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-159">
                        {fmtMoney(tooltipData.totalProfit)}
                      </span>
                    </div>
                  </div>
                </> : mode === 'roi' ? <>
                  <div className="space-y-1.5 text-xs" data-id="element-160">
                    <div className="flex justify-between gap-4" data-id="element-161">
                      <span className="text-gray-300" data-id="element-162">Total Invested:</span>
                      <span className="font-bold text-red-400" data-id="element-163">
                        {fmtMoney(tooltipData.totalInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-164">
                      <span className="text-gray-300" data-id="element-165">Total Revenue:</span>
                      <span className="font-bold text-green-400" data-id="element-166">
                        {fmtMoney(tooltipData.totalRevenue)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-167"></div>
                    <div className="flex justify-between gap-4" data-id="element-168">
                      <span className="text-gray-300" data-id="element-169">Gap:</span>
                      <span className={`font-bold ${tooltipData.totalRevenue >= tooltipData.totalInvestment ? 'text-green-400' : 'text-amber'}`} data-id="element-170">
                        {tooltipData.totalRevenue >= tooltipData.totalInvestment ? '+' : ''}
                        {fmtMoney(tooltipData.totalRevenue - tooltipData.totalInvestment)}
                      </span>
                    </div>
                    {tooltipData.totalRevenue >= tooltipData.totalInvestment && <div className="text-green-400 font-bold text-center mt-2" data-id="element-171">
                        ✓ ROI Achieved!
                      </div>}
                  </div>
                </> : <>
                  <div className="space-y-1.5 text-xs" data-id="element-172">
                    <div className="flex justify-between gap-4" data-id="element-173">
                      <span className="text-gray-300" data-id="element-174">Monthly Investment:</span>
                      <span className="font-bold text-red-400" data-id="element-175">
                        {fmtMoney(tooltipData.monthlyInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-176">
                      <span className="text-gray-300" data-id="element-177">Monthly Revenue:</span>
                      <span className="font-bold text-green-400" data-id="element-178">
                        {fmtMoney(tooltipData.monthlyRevenue)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-179"></div>
                    <div className="flex justify-between gap-4" data-id="element-180">
                      <span className="text-gray-300" data-id="element-181">Monthly Profit:</span>
                      <span className={`font-bold ${tooltipData.monthlyProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-182">
                        {fmtMoney(tooltipData.monthlyProfit)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-183"></div>
                    <div className="flex justify-between gap-4" data-id="element-184">
                      <span className="text-gray-300" data-id="element-185">Cumulative Profit:</span>
                      <span className={`font-bold ${tooltipData.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-186">
                        {fmtMoney(tooltipData.totalProfit)}
                      </span>
                    </div>
                  </div>
                </>}
            </div>
          </div>}
      </div>

      <div className="text-sm text-gray-600 mt-3" data-id="element-187">
        {mode === 'cumulative' ? <>
            <span className="font-semibold" data-id="element-188">Break-even:</span> {breakeven}
          </> : mode === 'roi' ? <span className="text-gray-600" data-id="element-189">
            Zoomed view showing when cumulative revenue exceeds cumulative
            investment
          </span> : <span className="text-gray-600" data-id="element-190">
            Lines cross when monthly revenue exceeds monthly investment
          </span>}
      </div>
    </div>;
}